import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import { type IResponseFullSurvey } from 'entities/Surveys/api/types'
import {
  EnrollmentFilters,
  EnrollmentStore,
  initEnrollmentFilters,
} from 'widgets/ContactEnrollment'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { SendFromStore } from 'widgets/SendFrom'
import type { IEnrollmentActions } from 'widgets/ContactEnrollment/store/types'

export class SurveyEnrollContactsStore {
  enrollmentFilters = EnrollmentFilters.Empty()
  enrollmentExcludeFilters = EnrollmentFilters.Empty()
  enrollmentStore: EnrollmentStore
  numberStore: SendFromStore
  error = ''
  loading = false
  disposeIsShowContactsAlert: IReactionDisposer | null = null

  constructor(readonly entities: IEntitiesStore) {
    this.numberStore = new SendFromStore(this.entities, {
      excludeDeprioritizedNumbers: true,
      getDropdownItemsOptions: {
        showOnlyVerifiedToolFree: true,
      },
      withDynamicSender: this.entities.featureFlagsStore.send_as_contact_owner,
    })
    this.enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () =>
            ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'survey',
              skipValues: ['tag'],
            }).then(({ data }) => data),
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
        excludeSearchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this.entities
    )

    this.initFilters()
    makeAutoObservable(this)

    this.reactionIsShowContactsAlert()
  }

  initFilters = () => this.enrollmentStore.initFilters()

  get paramsFilters(): IParamsFiltersListGroups['filtersListGroups'] {
    return {
      contacts: this.enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this.enrollmentFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this.enrollmentFilters.tags?.map((tag) => tag.id),
      advanced: this.enrollmentFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  get paramsExcludeFilters(): IParamsFiltersListGroups['filtersListGroups'] {
    return {
      contacts: this.enrollmentExcludeFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this.enrollmentExcludeFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this.enrollmentExcludeFilters.tags?.map((tag) => tag.id),
      advanced: this.enrollmentExcludeFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  init = async ({ filters, exclude_filters, send_from, settings }: IResponseFullSurvey) => {
    if (send_from) {
      void this.numberStore.setSendFrom(send_from)
    }

    runInAction(() => {
      this.loading = true
    })

    const [filtersData, excludeFiltersData] = await Promise.all([
      initEnrollmentFilters(this.entities, filters),
      initEnrollmentFilters(this.entities, exclude_filters),
    ])

    if (filtersData) {
      this.enrollmentFilters.sync(filtersData)
    }

    if (excludeFiltersData) {
      this.enrollmentExcludeFilters.sync(excludeFiltersData)
    }

    const isReEnroll = settings?.allow_re_entering?.value ?? false
    this.enrollmentStore.setIsContactReEnroll(isReEnroll)
    this.enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)

    runInAction(() => {
      this.loading = false
    })
  }

  setError = (value: string) => {
    this.error = value
  }

  editEnrollment = () => {
    const filters = this.enrollmentFilters.clone()
    const excludeFilters = this.enrollmentExcludeFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters: IEnrollmentActions['confirm'] = async ({ filters, excludeFilters }) => {
      runInAction(() => {
        this.enrollmentFilters = filters
        this.enrollmentExcludeFilters = excludeFilters
      })
      return true
    }

    this.enrollmentStore.openModal({
      filters: filters,
      excludeFilters: excludeFilters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
      allowEmptyCount: true,
      title: 'Contacts',
    })
  }

  dispose = () => {
    this.disposeIsShowContactsAlert?.()
  }

  reactionIsShowContactsAlert = () => {
    this.disposeIsShowContactsAlert = reaction(
      () => [this.enrollmentStore.isContactReEnroll, this.enrollmentFilters.filters],
      () => this.enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    )
  }
}
